const chapter = {
  id: 'indefinite-integration',
  slug: 'indefinite-integration',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE Main + Advanced',
  title: {
    english: 'Indefinite Integration',
        hindi: 'अनिश्चित समाकलन',
  },
  estReadMinutes: 65,
  difficulty: {
    english: 'Medium-Hard',
        hindi: 'मध्यम-कठिन',
  },
  weightage: {
    english: '2–3 questions in JEE Main',
        hindi: 'जेईई मेन में 2-3 प्रश्न',
  },
  syllabusTags: ['Calculus', 'JEE-Main', 'JEE-Advanced', 'Class-12'],
  sections: [
    {
      id: 'definition',
      title: {
        english: 'Antiderivative & the Integral',
        hindi: 'प्रतिअवकलज और समाकल',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Indefinite integral',
        hindi: 'अनिश्चितकालीन अभिन्न',
          },
          body: {
            english: `A function $F$ is an **antiderivative** of $f$ on an interval $I$ if $F'(x) = f(x)$ for all $x \\in I$. The collection of all antiderivatives is
$$\\int f(x)\\,dx = F(x) + C,$$
where $C$ is the **constant of integration**.`,
            hindi: 'एक फ़ंक्शन $F$ अंतराल $I$ पर $f$ का एक **एंटीडेरिवेटिव** है, यदि सभी $x \\in I$ के लिए $F\\\'(x) = f(x)$ है। सभी प्रतिअवकलजों का संग्रह है $$\\int f(x)\\,dx = F(x) + C,$$ जहां $C$ **एकीकरण का स्थिरांक** है।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Linearity',
        hindi: 'रैखिकता',
          },
          statement: {
            english: '$\\displaystyle \\int [a\\,f(x) + b\\,g(x)]\\,dx = a \\int f(x)\\,dx + b \\int g(x)\\,dx$.',
        hindi: '$\\displaystyle \\int [a\\,f(x) + b\\,g(x)]\\,dx = a \\int f(x)\\,dx + b \\int g(x)\\,dx$.',
          },
        },
      ],
    },
    {
      id: 'standard-integrals',
      title: {
        english: 'Standard Integrals',
        hindi: 'मानक समाकल',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Integral', 'Result'],
          rows: [
            ['$\\int x^n\\,dx$ ($n \\ne -1$)', '$\\dfrac{x^{n+1}}{n+1} + C$'],
            ['$\\int \\dfrac{1}{x}\\,dx$', '$\\ln |x| + C$'],
            ['$\\int e^x\\,dx$', '$e^x + C$'],
            ['$\\int a^x\\,dx$', '$\\dfrac{a^x}{\\ln a} + C$'],
            ['$\\int \\sin x\\,dx$', '$-\\cos x + C$'],
            ['$\\int \\cos x\\,dx$', '$\\sin x + C$'],
            ['$\\int \\sec^2 x\\,dx$', '$\\tan x + C$'],
            ['$\\int \\csc^2 x\\,dx$', '$-\\cot x + C$'],
            ['$\\int \\sec x \\tan x\\,dx$', '$\\sec x + C$'],
            ['$\\int \\csc x \\cot x\\,dx$', '$-\\csc x + C$'],
            ['$\\int \\tan x\\,dx$', '$\\ln |\\sec x| + C$'],
            ['$\\int \\cot x\\,dx$', '$\\ln |\\sin x| + C$'],
            ['$\\int \\sec x\\,dx$', '$\\ln |\\sec x + \\tan x| + C$'],
            ['$\\int \\csc x\\,dx$', '$\\ln |\\csc x - \\cot x| + C$'],
            ['$\\int \\dfrac{dx}{\\sqrt{1 - x^2}}$', '$\\sin^{-1} x + C$'],
            ['$\\int \\dfrac{dx}{1 + x^2}$', '$\\tan^{-1} x + C$'],
            ['$\\int \\dfrac{dx}{|x|\\sqrt{x^2 - 1}}$', '$\\sec^{-1} |x| + C$'],
            ['$\\int \\dfrac{dx}{x^2 - a^2}$', '$\\dfrac{1}{2a} \\ln \\left|\\dfrac{x-a}{x+a}\\right| + C$'],
            ['$\\int \\dfrac{dx}{a^2 - x^2}$', '$\\dfrac{1}{2a} \\ln \\left|\\dfrac{a+x}{a-x}\\right| + C$'],
            ['$\\int \\dfrac{dx}{x^2 + a^2}$', '$\\dfrac{1}{a} \\tan^{-1}(x/a) + C$'],
            ['$\\int \\dfrac{dx}{\\sqrt{a^2 - x^2}}$', '$\\sin^{-1}(x/a) + C$'],
            ['$\\int \\dfrac{dx}{\\sqrt{x^2 + a^2}}$', '$\\ln |x + \\sqrt{x^2 + a^2}| + C$'],
            ['$\\int \\dfrac{dx}{\\sqrt{x^2 - a^2}}$', '$\\ln |x + \\sqrt{x^2 - a^2}| + C$'],
            ['$\\int \\sqrt{a^2 - x^2}\\,dx$', '$\\dfrac{x}{2}\\sqrt{a^2 - x^2} + \\dfrac{a^2}{2}\\sin^{-1}(x/a) + C$'],
            ['$\\int \\sqrt{x^2 + a^2}\\,dx$', '$\\dfrac{x}{2}\\sqrt{x^2+a^2} + \\dfrac{a^2}{2}\\ln|x+\\sqrt{x^2+a^2}| + C$'],
            ['$\\int \\sqrt{x^2 - a^2}\\,dx$', '$\\dfrac{x}{2}\\sqrt{x^2-a^2} - \\dfrac{a^2}{2}\\ln|x+\\sqrt{x^2-a^2}| + C$'],
          ],
        },
      ],
    },
    {
      id: 'substitution',
      title: {
        english: 'Method of Substitution',
        hindi: 'प्रतिस्थापन की विधि',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Substitution rule',
        hindi: 'प्रतिस्थापन नियम',
          },
          statement: {
            english: `If $u = g(x)$ and $du = g'(x)\\,dx$, then
$$\\int f(g(x)) g'(x)\\,dx = \\int f(u)\\,du.$$`,
            hindi: 'यदि $u = g(x)$ और $du = g\\\'(x)\\,dx$, तो $$\\int f(g(x)) g\\\'(x)\\,dx = \\int f(u)\\,du.$$',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '$\\int \\dfrac{f\'(x)}{f(x)}\\,dx = \\ln |f(x)| + C$.',
        hindi: '$\\int \\dfrac{f\'(x)}{f(x)}\\,dx = \\ln |f(x)| + C$.',
            },
            {
              english: '$\\int [f(x)]^n f\'(x)\\,dx = \\dfrac{[f(x)]^{n+1}}{n+1} + C$ (for $n \\ne -1$).',
        hindi: '$\\int [f(x)]^n f\'(x)\\,dx = \\dfrac{[f(x)]^{n+1}}{n+1} + C$ ($n \\ne -1$ के लिए)।',
            },
            {
              english: 'Trig substitutions: $\\sqrt{a^2 - x^2} \\to x = a \\sin\\theta$, $\\sqrt{a^2 + x^2} \\to x = a \\tan\\theta$, $\\sqrt{x^2 - a^2} \\to x = a \\sec\\theta$.',
        hindi: 'ट्रिग प्रतिस्थापन: $\\sqrt{a^2 - x^2} \\to x = a \\sin\\theta$, $\\sqrt{a^2 + x^2} \\to x = a \\tan\\theta$, $\\sqrt{x^2 - a^2} \\to x = a \\sec\\theta$।',
            },
            {
              english: 'For $\\int R(\\sin x, \\cos x)\\,dx$ where $R$ is rational, try Weierstrass substitution $t = \\tan(x/2)$: $\\sin x = \\tfrac{2t}{1+t^2},\\ \\cos x = \\tfrac{1-t^2}{1+t^2},\\ dx = \\tfrac{2\\,dt}{1+t^2}$.',
        hindi: '$\\int R(\\sin x, \\cos x)\\,dx$ के लिए जहां $R$ तर्कसंगत है, वीयरस्ट्रैस प्रतिस्थापन $t = \\tan(x/2)$: $\\sin x = \\tfrac{2t}{1+t^2},\\ \\cos x = \\tfrac{1-t^2}{1+t^2},\\ dx = \\tfrac{2\\,dt}{1+t^2}$ का प्रयास करें।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Evaluate $\\displaystyle \\int \\dfrac{x}{\\sqrt{1 - x^4}}\\,dx$.',
        hindi: '$\\displaystyle \\int \\dfrac{x}{\\sqrt{1 - x^4}}\\,dx$ का मूल्यांकन करें.',
          },
          solution: {
            english: 'Let $u = x^2 \\Rightarrow du = 2x\\,dx$. Integral $= \\dfrac{1}{2}\\int \\dfrac{du}{\\sqrt{1 - u^2}} = \\dfrac{1}{2}\\sin^{-1}(x^2) + C$.',
        hindi: 'मान लें $u = x^2 \\Rightarrow du = 2x\\,dx$। तब समाकल $= \\dfrac{1}{2}\\int \\dfrac{du}{\\sqrt{1 - u^2}} = \\dfrac{1}{2}\\sin^{-1}(x^2) + C$.',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Evaluate $\\displaystyle \\int \\tan x\\,dx$.',
        hindi: '$\\displaystyle \\int \\tan x\\,dx$ का मूल्यांकन करें.',
          },
          solution: {
            english: '$= \\int \\dfrac{\\sin x}{\\cos x}\\,dx$. Let $u = \\cos x$, $du = -\\sin x\\,dx$. $= -\\ln|u| + C = -\\ln|\\cos x| + C = \\ln|\\sec x| + C$.',
        hindi: '$= \\int \\dfrac{\\sin x}{\\cos x}\\,dx$. मान लीजिए $u = \\cos x$, $du = -\\sin x\\,dx$। $= -\\ln|u| + C = -\\ln|\\cos x| + C = \\ln|\\sec x| + C$.',
          },
        },
      ],
    },
    {
      id: 'parts',
      title: {
        english: 'Integration by Parts',
        hindi: 'खण्डशः समाकलन',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Integration by parts',
        hindi: 'भागों द्वारा एकीकरण',
          },
          statement: {
            english: `$$\\int u\\,dv = uv - \\int v\\,du.$$
Choose $u$ using **ILATE** rule: **I**nverse trig, **L**ogarithm, **A**lgebraic, **T**rigonometric, **E**xponential — pick whatever appears earliest in this list as $u$.`,
            hindi: '$$\\int u\\,dv = uv - \\int v\\,du.$$ **ILATE** नियम का उपयोग करके $u$ चुनें: **I**nvers trig, **L**ogarithm, **A**lgebraic, **T**rigonometric, **E**xponential - इस सूची में जो भी सबसे पहले दिखाई दे उसे $u$ के रूप में चुनें।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Useful identity',
        hindi: 'उपयोगी पहचान',
          },
          statement: {
            english: '$\\displaystyle \\int e^x [f(x) + f\'(x)]\\,dx = e^x f(x) + C$.',
        hindi: '$\\displaystyle \\int e^x [f(x) + f\'(x)]\\,dx = e^x f(x) + C$.',
          },
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'Evaluate $\\displaystyle \\int x \\ln x\\,dx$.',
        hindi: '$\\displaystyle \\int x \\ln x\\,dx$ का मूल्यांकन करें.',
          },
          solution: {
            english: 'Let $u = \\ln x,\\ dv = x\\,dx$. Then $du = dx/x,\\ v = x^2/2$. Integral $= \\dfrac{x^2 \\ln x}{2} - \\int \\dfrac{x}{2}\\,dx = \\dfrac{x^2 \\ln x}{2} - \\dfrac{x^2}{4} + C$.',
        hindi: 'चलो $u = \\ln x,\\ dv = x\\,dx$. फिर $du = dx/x,\\ v = x^2/2$. इंटीग्रल $= \\dfrac{x^2 \\ln x}{2} - \\int \\dfrac{x}{2}\\,dx = \\dfrac{x^2 \\ln x}{2} - \\dfrac{x^2}{4} + C$.',
          },
        },
        {
          type: 'example',
          number: 4,
          problem: {
            english: 'Evaluate $\\displaystyle \\int e^x \\sin x\\,dx$.',
        hindi: '$\\displaystyle \\int e^x \\sin x\\,dx$ का मूल्यांकन करें.',
          },
          solution: {
            english: 'Apply by parts twice. Let $I = \\int e^x \\sin x\\,dx$. Then $I = e^x \\sin x - \\int e^x \\cos x\\,dx = e^x \\sin x - [e^x \\cos x + \\int e^x \\sin x\\,dx]$. So $2I = e^x(\\sin x - \\cos x)$ ⇒ $I = \\dfrac{e^x(\\sin x - \\cos x)}{2} + C$.',
        hindi: 'भागों द्वारा दो बार लगाएं। चलो $I = \\int e^x \\sin x\\,dx$. फिर $I = e^x \\sin x - \\int e^x \\cos x\\,dx = e^x \\sin x - [e^x \\cos x + \\int e^x \\sin x\\,dx]$. तो $2I = e^x(\\sin x - \\cos x)$ ⇒ $I = \\dfrac{e^x(\\sin x - \\cos x)}{2} + C$.',
          },
        },
        {
          type: 'example',
          number: 5,
          problem: {
            english: 'Evaluate $\\displaystyle \\int e^x \\left(\\dfrac{1 + \\sin x}{1 + \\cos x}\\right)\\,dx$.',
        hindi: '$\\displaystyle \\int e^x \\left(\\dfrac{1 + \\sin x}{1 + \\cos x}\\right)\\,dx$ का मूल्यांकन करें.',
          },
          solution: {
            english: 'Use half-angle: $\\dfrac{1+\\sin x}{1+\\cos x} = \\dfrac{1}{2}\\sec^2(x/2) + \\tan(x/2)$. Now $f(x) = \\tan(x/2),\\ f\'(x) = \\tfrac{1}{2}\\sec^2(x/2)$, matching the identity. Integral $= e^x \\tan(x/2) + C$.',
        hindi: 'अर्ध-कोण का उपयोग करें: $\\dfrac{1+\\sin x}{1+\\cos x} = \\dfrac{1}{2}\\sec^2(x/2) + \\tan(x/2)$। अब $f(x) = \\tan(x/2),\\ f\'(x) = \\tfrac{1}{2}\\sec^2(x/2)$, पहचान से मिलान। इंटीग्रल $= e^x \\tan(x/2) + C$.',
          },
        },
      ],
    },
    {
      id: 'partial-fractions',
      title: {
        english: 'Integration of Rational Functions (Partial Fractions)',
        hindi: 'परिमेय फलनों का समाकलन (आंशिक भिन्न)',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'For a proper rational function $P(x)/Q(x)$, factor $Q$ and decompose into partial fractions:',
        hindi: 'एक उचित परिमेय फलन $P(x)/Q(x)$ के लिए, गुणनखंड $Q$ करें और आंशिक भिन्नों में विघटित करें:',
          },
        },
        {
          type: 'table',
          headers: ['Factor in $Q(x)$', 'Partial fraction'],
          rows: [
            ['Distinct linear $(x - a)$', '$\\dfrac{A}{x - a}$'],
            ['Repeated linear $(x - a)^k$', '$\\dfrac{A_1}{x-a} + \\dfrac{A_2}{(x-a)^2} + \\ldots + \\dfrac{A_k}{(x-a)^k}$'],
            ['Distinct quadratic $(x^2 + bx + c)$', '$\\dfrac{Bx + C}{x^2 + bx + c}$'],
            ['Repeated quadratic $(x^2 + bx + c)^k$', 'Sum of $\\dfrac{B_i x + C_i}{(x^2+bx+c)^i}$ for $i = 1, \\ldots, k$'],
          ],
        },
        {
          type: 'example',
          number: 6,
          problem: {
            english: 'Evaluate $\\displaystyle \\int \\dfrac{x + 1}{x^2 - 5x + 6}\\,dx$.',
        hindi: '$\\displaystyle \\int \\dfrac{x + 1}{x^2 - 5x + 6}\\,dx$ का मूल्यांकन करें.',
          },
          solution: {
            english: 'Factor: $x^2 - 5x + 6 = (x - 2)(x - 3)$. Decompose: $\\dfrac{x+1}{(x-2)(x-3)} = \\dfrac{-3}{x-2} + \\dfrac{4}{x-3}$. Integral $= -3 \\ln|x-2| + 4 \\ln|x-3| + C$.',
        hindi: 'फ़ैक्टर: $x^2 - 5x + 6 = (x - 2)(x - 3)$. विघटित करें: $\\dfrac{x+1}{(x-2)(x-3)} = \\dfrac{-3}{x-2} + \\dfrac{4}{x-3}$. इंटीग्रल $= -3 \\ln|x-2| + 4 \\ln|x-3| + C$.',
          },
        },
      ],
    },
    {
      id: 'special-forms',
      title: {
        english: 'Special Forms & Tricks',
        hindi: 'विशेष रूप और युक्तियाँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '$\\int \\dfrac{dx}{ax^2 + bx + c}$: complete the square in denominator, then use one of $\\tfrac{dx}{x^2 \\pm a^2}$ formulas.',
        hindi: '$\\int \\dfrac{dx}{ax^2 + bx + c}$: हर में वर्ग पूरा करें, फिर $\\tfrac{dx}{x^2 \\pm a^2}$ सूत्रों में से एक का उपयोग करें।',
            },
            {
              english: '$\\int \\dfrac{dx}{\\sqrt{ax^2 + bx + c}}$: similar — complete the square.',
        hindi: '$\\int \\dfrac{dx}{\\sqrt{ax^2 + bx + c}}$: समान - वर्ग पूरा करें।',
            },
            {
              english: '$\\int \\dfrac{px + q}{ax^2 + bx + c}\\,dx$: write $px + q = \\lambda \\dfrac{d}{dx}(ax^2 + bx + c) + \\mu$ then split into two standard forms.',
        hindi: '$\\int \\dfrac{px + q}{ax^2 + bx + c}\\,dx$: $px + q = \\lambda \\dfrac{d}{dx}(ax^2 + bx + c) + \\mu$ लिखें फिर दो मानक रूपों में विभाजित करें।',
            },
            {
              english: '$\\int \\dfrac{dx}{a + b \\sin x}$ or $\\dfrac{dx}{a + b \\cos x}$: use Weierstrass substitution $t = \\tan(x/2)$.',
        hindi: '$\\int \\dfrac{dx}{a + b \\sin x}$ या $\\dfrac{dx}{a + b \\cos x}$: वीयरस्ट्रैस प्रतिस्थापन $t = \\tan(x/2)$ का उपयोग करें।',
            },
            {
              english: '$\\int \\dfrac{dx}{a \\sin^2 x + b \\cos^2 x}$: divide num and denom by $\\cos^2 x$, then substitute $t = \\tan x$.',
        hindi: '$\\int \\dfrac{dx}{a \\sin^2 x + b \\cos^2 x}$: संख्या और संप्रदाय को $\\cos^2 x$ से विभाजित करें, फिर $t = \\tan x$ प्रतिस्थापित करें।',
            },
          ],
        },
        {
          type: 'example',
          number: 7,
          problem: {
            english: 'Evaluate $\\displaystyle \\int \\dfrac{dx}{x^2 + 4x + 13}$.',
        hindi: '$\\displaystyle \\int \\dfrac{dx}{x^2 + 4x + 13}$ का मूल्यांकन करें.',
          },
          solution: {
            english: `$x^2 + 4x + 13 = (x + 2)^2 + 9$. Substitute $u = x + 2$:
$\\int \\dfrac{du}{u^2 + 9} = \\dfrac{1}{3}\\tan^{-1}\\left(\\dfrac{u}{3}\\right) + C = \\dfrac{1}{3}\\tan^{-1}\\left(\\dfrac{x + 2}{3}\\right) + C$.`,
            hindi: '$x^2 + 4x + 13 = (x + 2)^2 + 9$। स्थानापन्न $u = x + 2$: $\\int \\dfrac{du}{u^2 + 9} = \\dfrac{1}{3}\\tan^{-1}\\left(\\dfrac{u}{3}\\right) + C = \\dfrac{1}{3}\\tan^{-1}\\left(\\dfrac{x + 2}{3}\\right) + C$.',
          },
        },
        {
          type: 'example',
          number: 8,
          problem: {
            english: 'Evaluate $\\displaystyle \\int \\dfrac{dx}{5 + 4 \\cos x}$.',
        hindi: '$\\displaystyle \\int \\dfrac{dx}{5 + 4 \\cos x}$ का मूल्यांकन करें.',
          },
          solution: {
            english: 'Let $t = \\tan(x/2)$. $\\cos x = \\dfrac{1 - t^2}{1 + t^2},\\ dx = \\dfrac{2\\,dt}{1+t^2}$. Integral $= \\int \\dfrac{2\\,dt}{(1+t^2)\\left[5 + 4\\tfrac{1-t^2}{1+t^2}\\right]} = \\int \\dfrac{2\\,dt}{5(1+t^2) + 4(1-t^2)} = \\int \\dfrac{2\\,dt}{9 + t^2} = \\dfrac{2}{3}\\tan^{-1}(t/3) + C = \\dfrac{2}{3}\\tan^{-1}\\left(\\dfrac{\\tan(x/2)}{3}\\right) + C$.',
        hindi: 'चलो $t = \\tan(x/2)$. $\\cos x = \\dfrac{1 - t^2}{1 + t^2},\\ dx = \\dfrac{2\\,dt}{1+t^2}$. इंटीग्रल $= \\int \\dfrac{2\\,dt}{(1+t^2)\\left[5 + 4\\tfrac{1-t^2}{1+t^2}\\right]} = \\int \\dfrac{2\\,dt}{5(1+t^2) + 4(1-t^2)} = \\int \\dfrac{2\\,dt}{9 + t^2} = \\dfrac{2}{3}\\tan^{-1}(t/3) + C = \\dfrac{2}{3}\\tan^{-1}\\left(\\dfrac{\\tan(x/2)}{3}\\right) + C$.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\int x^n\\,dx = \\tfrac{x^{n+1}}{n+1} + C',
      desc: {
        english: 'Power rule ($n \\ne -1$)',
        hindi: 'पावर नियम ($n \\ne -1$)',
      },
    },
    {
      latex: '\\int \\tfrac{f\'}{f}\\,dx = \\ln |f| + C',
      desc: {
        english: 'Logarithmic substitution',
        hindi: 'लघुगणकीय प्रतिस्थापन',
      },
    },
    {
      latex: '\\int u\\,dv = uv - \\int v\\,du',
      desc: {
        english: 'Integration by parts',
        hindi: 'भागों द्वारा एकीकरण',
      },
    },
    {
      latex: '\\int e^x[f + f\']\\,dx = e^x f + C',
      desc: {
        english: 'Special exponential identity',
        hindi: 'विशेष घातीय पहचान',
      },
    },
    {
      latex: '\\int \\tfrac{dx}{x^2+a^2} = \\tfrac{1}{a}\\tan^{-1}(x/a) + C',
      desc: {
        english: '',
        hindi: 'x 2+a 2 = a -1 (x/a) + C',
      },
    },
    {
      latex: '\\int \\tfrac{dx}{\\sqrt{a^2-x^2}} = \\sin^{-1}(x/a) + C',
      desc: {
        english: '',
        hindi: '= -1 (x/a) + C',
      },
    },
    {
      latex: '\\int \\sec x\\,dx = \\ln|\\sec x + \\tan x| + C',
      desc: {
        english: '',
        hindi: 'x\\,dx = | एक्स + एक्स| + सी',
      },
    },
    {
      latex: 'ILATE',
      desc: {
        english: 'Order to choose u in by-parts',
        hindi: 'आपको उप-भागों में चुनने का आदेश',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Substitution',
      problem: {
        english: 'Evaluate $\\displaystyle \\int \\dfrac{\\sec^2 x}{\\sqrt{1 + \\tan x}}\\,dx$.',
        hindi: '$\\displaystyle \\int \\dfrac{\\sec^2 x}{\\sqrt{1 + \\tan x}}\\,dx$ का मूल्यांकन करें.',
      },
      answer: {
        english: '$2\\sqrt{1 + \\tan x} + C$',
        hindi: '$2\\sqrt{1 + \\tan x} + C$',
      },
      solution: {
        english: 'Let $u = 1 + \\tan x \\Rightarrow du = \\sec^2 x\\,dx$. $\\int u^{-1/2}\\,du = 2\\sqrt u + C$.',
        hindi: 'चलो $u = 1 + \\tan x \\Rightarrow du = \\sec^2 x\\,dx$. $\\int u^{-1/2}\\,du = 2\\sqrt u + C$.',
      },
    },
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'By parts',
      problem: {
        english: '$\\displaystyle \\int \\ln x\\,dx = ?$',
        hindi: '$\\displaystyle \\int \\ln x\\,dx =?$',
      },
      answer: {
        english: '$x \\ln x - x + C$',
        hindi: '$x \\ln x - x + C$',
      },
      solution: {
        english: '$u = \\ln x,\\ dv = dx \\Rightarrow uv - \\int v\\,du = x \\ln x - \\int 1\\,dx$.',
        hindi: '$u = \\ln x,\\ dv = dx \\Rightarrow uv - \\int v\\,du = x \\ln x - \\int 1\\,dx$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Partial fractions',
      problem: {
        english: '$\\displaystyle \\int \\dfrac{dx}{x(x^2 + 1)} = ?$',
        hindi: '$\\displaystyle \\int \\dfrac{dx}{x(x^2 + 1)} =?$',
      },
      answer: {
        english: '$\\ln |x| - \\dfrac{1}{2}\\ln(1 + x^2) + C$',
        hindi: '$\\ln |x| - \\dfrac{1}{2}\\ln(1 + x^2) + C$',
      },
      solution: {
        english: 'Decompose: $\\dfrac{1}{x(x^2+1)} = \\dfrac{1}{x} - \\dfrac{x}{x^2+1}$.',
        hindi: 'विघटित करें: $\\dfrac{1}{x(x^2+1)} = \\dfrac{1}{x} - \\dfrac{x}{x^2+1}$.',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Special form',
      problem: {
        english: '$\\displaystyle \\int \\dfrac{dx}{\\sqrt{x^2 + 6x + 13}} = ?$',
        hindi: '$\\displaystyle \\int \\dfrac{dx}{\\sqrt{x^2 + 6x + 13}} =?$',
      },
      answer: {
        english: '$\\ln |(x + 3) + \\sqrt{x^2 + 6x + 13}| + C$',
        hindi: '$\\ln |(x + 3) + \\sqrt{x^2 + 6x + 13}| + C$',
      },
      solution: {
        english: 'Complete square: $(x+3)^2 + 4$. Standard form $\\int \\dfrac{du}{\\sqrt{u^2 + a^2}}$.',
        hindi: 'पूर्ण वर्ग: $(x+3)^2 + 4$। मानक प्रपत्र $\\int \\dfrac{du}{\\sqrt{u^2 + a^2}}$.',
      },
    },
    {
      year: 2020,
      exam: 'JEE Advanced',
      tag: 'e^x identity',
      problem: {
        english: 'Evaluate $\\displaystyle \\int e^x \\dfrac{x - 1}{x^2}\\,dx$.',
        hindi: '$\\displaystyle \\int e^x \\dfrac{x - 1}{x^2}\\,dx$ का मूल्यांकन करें.',
      },
      answer: {
        english: '$\\dfrac{e^x}{x} + C$',
        hindi: '$\\dfrac{e^x}{x} + C$',
      },
      solution: {
        english: '$\\dfrac{x-1}{x^2} = \\dfrac{1}{x} - \\dfrac{1}{x^2}$. With $f(x) = 1/x,\\ f\'(x) = -1/x^2$, the bracket is $f + f\'$. Hence $e^x f(x) + C = e^x/x + C$.',
        hindi: '$\\dfrac{x-1}{x^2} = \\dfrac{1}{x} - \\dfrac{1}{x^2}$. $f(x) = 1/x,\\ f\'(x) = -1/x^2$ के साथ, ब्रैकेट $f + f\'$ है। इसलिए $e^x f(x) + C = e^x/x + C$.',
      },
    },
  ],
  next: {
    slug: 'definite-integration-areas',
    title: 'Definite Integration & Areas',
  },
};

export default chapter;
